import { ClearOutlined, KeyboardReturn, SearchOutlined } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Chip,
  ClickAwayListener,
  Divider,
  InputBase,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import GridItemCount from '@sophocles/data-grids/components/cells/GridItemCount';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/**
 * Unified scope-filter + free-text search input.
 *
 * Renders a search input with applied scope filters as chips inside the
 * input container. As the user types, each provider asynchronously
 * surfaces suggestions in a sectioned popover; picking a suggestion
 * converts it into a chip via `provider.applyValue` and clears the
 * input. Free-text "Search 'X'" sits at the bottom of the popover and
 * is dispatched via `onSubmit`.
 *
 * Props
 *   value          – current input string (controlled)
 *   onValueChange  – (str) => void
 *   chips          – [{ key, label, displayValue, icon?, color?, onRemove }]
 *   providers      – [{
 *       key,                                      // unique
 *       label,                                    // section header
 *       icon?,                                    // suggestion icon
 *       fetchSuggestions: async (query) => [{ id, label, sublabel? }],
 *       applyValue: (option) => void,             // dispatches the filter change
 *       isActive?: (chips) => boolean,            // hide section when scope is already pinned
 *       maxResults?: number,                      // defaults to 5
 *     }]
 *   placeholder    – string
 *   onSubmit       – (query) => void   // called on Enter / "Search 'X'" row
 *   submitLabel    – text in the fallback row (default: "Search 'X'")
 *   minQueryLength – override the default (2 chars)
 *   sx             – container sx overrides
 */
export default function ScopeSearchInput({
  value,
  onValueChange,
  chips = [],
  providers = [],
  placeholder = 'Search…',
  onSubmit,
  submitLabel,
  minQueryLength = MIN_QUERY_LENGTH,
  itemCount,
  countNoun,
  sx,
}) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const latestQueryRef = useRef('');
  const debounceTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  // { [providerKey]: { loading: boolean, items: [] } }
  const [suggestions, setSuggestions] = useState({});

  const activeProviders = useMemo(() => providers.filter(p => !p.isActive || !p.isActive(chips)), [providers, chips]);

  // Cleanup pending timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  // Debounced query runner. Each provider fetches independently; stale
  // responses are discarded by comparing against latestQueryRef.
  const runQuery = useCallback(
    query => {
      latestQueryRef.current = query;
      if (!query || query.trim().length < minQueryLength) {
        setSuggestions({});
        return;
      }
      activeProviders.forEach(provider => {
        const maxResults = provider.maxResults ?? 5;
        setSuggestions(prev => ({
          ...prev,
          [provider.key]: { loading: true, items: prev[provider.key]?.items ?? [] },
        }));
        Promise.resolve(provider.fetchSuggestions(query))
          .then(items => {
            if (latestQueryRef.current !== query) return; // stale
            setSuggestions(prev => ({
              ...prev,
              [provider.key]: { loading: false, items: (items ?? []).slice(0, maxResults) },
            }));
          })
          .catch(() => {
            if (latestQueryRef.current !== query) return;
            setSuggestions(prev => ({ ...prev, [provider.key]: { loading: false, items: [] } }));
          });
      });
    },
    [activeProviders, minQueryLength],
  );

  // Debounce on input
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => runQuery(value), DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [value, runQuery]);

  const popoverHasContent = useMemo(
    () =>
      activeProviders.some(p => {
        const state = suggestions[p.key];
        return state?.loading || (state?.items?.length ?? 0) > 0;
      }),
    [activeProviders, suggestions],
  );

  const shouldShowPopover = open && value.trim().length >= minQueryLength && (popoverHasContent || onSubmit);

  const handleSelectSuggestion = useCallback(
    (provider, option) => {
      provider.applyValue(option);
      onValueChange('');
      setOpen(false);
      setSuggestions({});
      // Keep focus in the input for a fluent typing flow
      inputRef.current?.focus();
    },
    [onValueChange],
  );

  // The input sits outside the suggestion surface, so a click on it reads as a
  // click away; dismissing there would fight the refocus and flicker.
  const handleClickAway = useCallback(event => {
    if (containerRef.current?.contains(event.target)) return;
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!onSubmit) return;
    // An empty submit commits too — Enter on a cleared input resets the
    // grid's search instead of leaving it stuck on the last results.
    onSubmit(value.trim());
    setOpen(false);
  }, [onSubmit, value]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'Backspace' && !value && chips.length > 0) {
        e.preventDefault();
        chips[chips.length - 1].onRemove?.();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [value, chips, handleSubmit],
  );

  return (
    <Box
      ref={containerRef}
      onClick={() => inputRef.current?.focus()}
      sx={{
        // Matches the filled-variant chrome of ToolbarSearchInput so the
        // unified input visually pairs with the rest of the grid toolbar.
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        borderRadius: 'var(--button-radius)',
        border: '1px solid var(--button-outline-border)',
        backgroundColor: 'var(--color-gray-50)',
        px: 1,
        py: 0.5,
        minHeight: 36,
        flex: 1,
        minWidth: 0,
        cursor: 'text',
        transition:
          'background-color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard)',
        '&:hover': { backgroundColor: 'var(--color-gray-50)' },
        '&:focus-within': {
          backgroundColor: 'var(--color-white)',
          borderColor: 'var(--button-outline-border-hover)',
        },
        ...sx,
      }}
    >
      <SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />

      {chips.map(chip => (
        <Chip
          key={chip.key}
          size="small"
          icon={chip.icon}
          color={chip.color || 'default'}
          variant="filled"
          label={
            <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'baseline' }}>
              {chip.label && (
                <Typography component="span" variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
                  {chip.label}:
                </Typography>
              )}
              <Typography component="span" variant="caption">
                {chip.displayValue}
              </Typography>
            </Box>
          }
          onDelete={chip.onRemove}
          sx={{ height: 22, borderRadius: 'var(--radius-md)' }}
        />
      ))}

      <InputBase
        inputRef={inputRef}
        value={value}
        placeholder={chips.length === 0 ? placeholder : ''}
        onChange={e => {
          onValueChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (value.trim().length >= minQueryLength) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        sx={{ flex: 1, minWidth: 80, fontSize: '0.875rem', '& input': { p: 0 } }}
      />

      {itemCount != null && <GridItemCount count={itemCount} noun={countNoun} sx={{ pl: 0.5 }} />}

      {value && (
        <>
          <ClearOutlined
            aria-label="Clear search"
            onClick={e => {
              e.stopPropagation();
              onValueChange('');
              setOpen(false);
              // Clearing also commits the empty state so the grid filter resets.
              onSubmit?.('');
              inputRef.current?.focus();
            }}
            sx={{
              fontSize: 16,
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': { color: 'text.primary' },
            }}
          />
          {onSubmit && (
            <Tooltip title="Search">
              <ButtonBase
                onMouseDown={e => e.preventDefault() /* don't blur input */}
                onClick={handleSubmit}
                aria-label="Search"
                sx={{
                  ml: 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  height: 22,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'var(--radius-sm)',
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: 0.2,
                  fontFamily: theme => theme.typography.fontFamily,
                  transition:
                    'background-color var(--duration-normal) var(--easing-standard), color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary',
                    borderColor: 'text.secondary',
                  },
                }}
              >
                <KeyboardReturn sx={{ fontSize: 12 }} />
                Enter
              </ButtonBase>
            </Tooltip>
          )}
        </>
      )}

      {/* Popper, not Popover: a modal surface lays a backdrop over the page, so
          every click meant for something else is swallowed dismissing it, and
          the focus it hands back reopens the suggestions on the way out. */}
      <Popper
        open={shouldShowPopover}
        anchorEl={containerRef.current}
        placement="bottom-start"
        sx={{ zIndex: 'modal' }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            sx={{
              mt: 0.5,
              minWidth: containerRef.current?.offsetWidth ?? 320,
              maxWidth: 480,
              maxHeight: 420,
              overflow: 'auto',
            }}
          >
            {activeProviders.map(provider => {
              const state = suggestions[provider.key];
              if (!state) return null;
              const items = state.items ?? [];
              if (!state.loading && items.length === 0) return null;
              return (
                <Box key={provider.key}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      pt: 1,
                      pb: 0.25,
                      display: 'block',
                      color: 'text.secondary',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontSize: '0.7rem',
                    }}
                  >
                    {provider.label}
                  </Typography>
                  {state.loading && items.length === 0 ? (
                    <Stack spacing={0.5} sx={{ px: 2, py: 0.5 }}>
                      <Skeleton variant="rounded" height={24} />
                      <Skeleton variant="rounded" height={24} width="80%" />
                    </Stack>
                  ) : (
                    <MenuList dense disablePadding>
                      {items.map(opt => (
                        <MenuItem
                          key={opt.id ?? opt.value}
                          onMouseDown={e => e.preventDefault() /* don't blur input */}
                          onClick={() => handleSelectSuggestion(provider, opt)}
                        >
                          {provider.icon && (
                            <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>{provider.icon}</ListItemIcon>
                          )}
                          <ListItemText
                            primary={opt.label}
                            secondary={opt.sublabel}
                            slotProps={{
                              primary: { variant: 'body2' },
                              secondary: { variant: 'caption' },
                            }}
                          />
                        </MenuItem>
                      ))}
                    </MenuList>
                  )}
                </Box>
              );
            })}

            {onSubmit && value.trim() && (
              <>
                {popoverHasContent && <Divider />}
                <MenuItem onMouseDown={e => e.preventDefault()} onClick={handleSubmit} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                    <SearchOutlined sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={submitLabel ? `${submitLabel} "${value.trim()}"` : `Search "${value.trim()}"`}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                </MenuItem>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
